<script setup lang="ts">
import { onMounted, watch } from 'vue'
import type { Note } from '../types/note'
import type { BibleVersion, BibleBookOption } from '../types/bible'
import { useGlobalBibleData } from '../composables/useGlobalBibleData'
import { formatDateTimeInTimezone } from '../utils/datetimeUtil'

interface Props {
  notes: Note[]
  versions: BibleVersion[]
  books: BibleBookOption[]
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<{
  select: [note: Note]
  delete: [id: number]
}>()

// 從全局數據源獲取版本和書籍信息，作為備用
const { versions: globalVersions, books: globalBooks, loadVersions, loadBooks } = useGlobalBibleData()

const loadBibleData = async () => {
  // 確保全局數據被加載
  await loadVersions()

  // 為所有涉及的版本加載書籍
  const versionCodes = new Set(props.notes.map(n => n.versionCode).filter(v => v))
  for (const versionCode of versionCodes) {
    await loadBooks(versionCode)
  }
}

onMounted(() => {
  // 首次掛載時加載
  if (props.notes.length > 0) {
    loadBibleData()
  }
})

// 監聽 notes 變化，當有筆記時加載書籍
watch(() => props.notes, (newNotes) => {
  if (newNotes.length > 0) {
    loadBibleData()
  }
}, { immediate: true })

const formatTime = (utcDatetime: string): string => {
  return formatDateTimeInTimezone(utcDatetime, 'Asia/Taipei', 'YYYY-MM-DD HH:MM:SS')
}

const getVerseInfo = (note: Note): string => {
  // 優先使用傳入的 props，再使用全局數據
  let version = props.versions.find(v => v.code === note.versionCode)
  if (!version) {
    version = globalVersions.value.find(v => v.code === note.versionCode)
  }

  let book = props.books.find(b => b.id === note.bookId)
  if (!book) {
    book = globalBooks.value.find(b => b.id === note.bookId)
  }

  if (!version || !book) {
    return '筆記'
  }

  return `${book.chiFull}${note.chapterNumber}:${note.verseStart}（${version.name}）`
}
</script>

<template>
  <div class="note-list-panel">
    <div v-if="loading" class="list-state">
      加載中...
    </div>
    <div v-else-if="notes.length === 0" class="list-state">
      暫無筆記
    </div>
    <div v-else class="list-container">
      <div
        v-for="note in notes"
        :key="note.id"
        class="note-item"
        @click="$emit('select', note)"
      >
        <div class="note-content">
          <div class="note-verse">{{ getVerseInfo(note) }}</div>
          <div class="note-time">{{ formatTime(note.updatedAt) }}</div>
        </div>
        <button
          class="delete-btn"
          title="刪除"
          @click.stop="$emit('delete', note.id)"
        >
          ✕
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.note-list-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background-color: white;
  border-left: 1px solid #e0e0e0;
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
  min-height: 44px;
}

.panel-header h2 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.list-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 12px;
}

.list-container {
  flex: 1;
  overflow-y: auto;
}

.note-item {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.note-item:hover {
  background-color: #f9f9f9;
}

.note-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.note-verse {
  font-size: 12px;
  color: #333;
  font-weight: 500;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-time {
  font-size: 11px;
  color: #999;
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #999;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: all 0.2s;
}

.delete-btn:hover {
  background-color: #ff4444;
  color: white;
}
</style>
